import React from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage, StaticImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { BsTwitter } from 'react-icons/bs';
import { FaLink } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import generateHoleTweet from '../../lib/generateHoleTweet';
import copyUrl from '../../lib/copyUrl';

const HoleWrapper = styled.div`
  border: 10px solid var(--custom-green);
  max-width: 900px;
  width: calc(100% - 40px);
  margin: 0 auto 2rem;
  &.no-show {
    display: none;
  }
  @media(max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const HoleImageWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  border-bottom: 10px solid var(--custom-green);
`;

const HoleImage = styled.div`
  width: 100%;
`;

const HoleContent = styled.div`
  padding: 2rem;
  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-column-gap: 1rem;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    grid-row-gap: 2rem;
  }
  .golfers__wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 1rem;
    @media (max-width: 800px) {
      border-top: 5px solid var(--custom-green);
      padding-top: 2rem;
      grid-column-gap: 0rem;
    }
    @media (max-width: 500px) {
      display: block;
    }
  }
  .hole__section {
    font-family: var(--block-font);
    &:last-of-type {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }
    h3 {
      font-size: clamp(2rem, 7vw, 3.5rem);
      line-height: clamp(2.5rem 8vw, 4rem);
      margin-bottom: 0.5rem;
      color: var(--custom-green);
      letter-spacing: 1px;
      @media (max-width: 800px) {
        letter-spacing: 0.25px;
      }
    }
    h4 {
      font-size: 1.25rem;
      line-height: 1.625rem;
      letter-spacing: 0.5px;
      @media (max-width: 800px) {
        letter-spacing: 0.1px;
      }
    }
    h5 {
      font-size: 1.125rem;
      line-height: 1.25rem;
      margin: 0.5rem 0;
      letter-spacing: 0.25px
      @media (max-width: 800px) {
        letter-spacing: 0px;
      }
    }
    p, li {
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: rgb(66,66,66);
    }
    li {
      margin-bottom: 1.125rem;
      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }
  .hole__player {
    border-left: 5px solid var(--custom-green);
    padding: 0 1rem;
    @media (max-width: 800px) {
      border-left: none;
      padding: 0 1rem;
      &:first-of-type {
        border-right: 2px solid var(--custom-green);
      }
      &:last-of-type {
        border-left: 2px solid var(--custom-green);
      }
    }
    @media (max-width: 500px) {
      img {
        width: 50%;
        margin: 0 auto;
      }
      border-right: none !important;
      border-left: none !important;
      margin-bottom: 1rem;
      padding-bottom: 1rem;
      border-bottom: 5px solid var(--custom-green);
      &:last-of-type {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
      }
    }
    h4 {
      margin-top: 0.5rem;
    }
  }
`;

const Shareables = styled.div`
  margin: 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgb(33,33,33);
  font-size: 1.25rem;
  a, button {
    padding: 0 5px;
    color: var(--custom-green);
    cursor: pointer;
    &:hover {
      color var(--custom-green);
    }
  }
  button {
    background-color: initial;
    border: none;
    font-size: 1.25rem;
    position: relative;
  }
`;

const CopyMarker = styled.div`
  opacity: 0;
  top: -0.5rem;
  left: 50%;
  transform: translateX(-50%);
  background-color: black;
  color: white;
  position: absolute;
  transition: all 0.2s ease-out;
  pointer-events: none;
  font-size: 0.75rem;
  padding: 0.25rem;
  border-radius: 3px;
  span {
    position: relative;
    z-index: 10;
  }
  &:after {
    width: 12px;
    height: 12px;
    transform: rotate(45deg) translateX(-50%);
    background-color: black;
    position: absolute;
    bottom: -6px;
    left: 50%;
    content: '';
    display: block;
  }
  &.displayed {
    opacity: 1;
    top: -2rem;
  }
`;

const Hole = ({
  data, currentHole, copied, markCopied, url,
}) => {
  const shareUrl = () => {
    copyUrl(currentHole, url);

    markCopied(true);

    setTimeout(() => {
      markCopied(false);
    }, 2000);
  };

  const holeOverviewContent = <ReactMarkdown>{data.holeOverview}</ReactMarkdown>;
  const golferContent = <ReactMarkdown>{data.golfer}</ReactMarkdown>;
  const fanContent = <ReactMarkdown>{data.fan}</ReactMarkdown>;
  const didYouKnowContent = <ReactMarkdown>{data.didYouKnow}</ReactMarkdown>;

  return (
    <HoleWrapper className={currentHole === parseInt(data.hole, 10) ? null : 'no-show'}>
      <HoleImageWrapper className="horizontal">
        <HoleImage>
          <GatsbyImage
            alt={`Illustration of Hole ${data.hole}`}
            image={getImage(data.rotatedImage.childImageSharp)}
          />
        </HoleImage>
      </HoleImageWrapper>
      <HoleContent>
        <div className="hole__section hole__meta">
          <h3>Hole {data.hole}</h3>
          <h5>Par {data.par} / {data.yards} yards</h5>
          {data.hole !== undefined && (
            <Shareables className="shareables">
              <a
                href={generateHoleTweet(data.hole, url)}
                target="_blank"
                rel="noreferrer"
                className="benton-n4"
              >
                <BsTwitter />
              </a>
              <button type="button" onClick={shareUrl}>
                <FaLink />
                <CopyMarker
                  className={copied ? 'displayed bentonsanscond-n4' : 'bentonsanscond-n4'}
                  aria-hidden={copied ? 'false' : 'true'}
                >
                  <span>Copied!</span>
                </CopyMarker>
              </button>
            </Shareables>
          )}
          <p className="benton-n4">{holeOverviewContent}</p>
          <div className="hole__section hole__blurbs">
            <h5>Did you know?</h5>
            <ul>
              <li className="benton-n4">{didYouKnowContent}</li>
            </ul>
          </div>
        </div>
        <div className="golfers__wrapper">
          <div className="hole__section hole__player hole__fan">
            <StaticImage
              src="../../assets/images/tiger.jpg"
              alt="Tiger Woods"
              width={600}
            />
            <h4>For the golfer</h4>
            <p className="benton-n4">{golferContent}</p>
          </div>
          <div className="hole__section hole__player hole__hacker">
            <StaticImage
              src="../../assets/images/happy.jpg"
              alt="Happy Gilmore"
              width={600}
            />
            <h4>For the fan</h4>
            <p className="benton-n4">{fanContent}</p>
          </div>
        </div>
      </HoleContent>
    </HoleWrapper>
  );
};

Hole.propTypes = {
  data: PropTypes.shape({
    hole: PropTypes.string.isRequired,
    par: PropTypes.string.isRequired,
    yards: PropTypes.string.isRequired,
    holeOverview: PropTypes.string.isRequired,
    fan: PropTypes.string.isRequired,
    golfer: PropTypes.string.isRequired,
    didYouKnow: PropTypes.string.isRequired,
  }).isRequired,
  currentHole: PropTypes.number.isRequired,
  copied: PropTypes.bool.isRequired,
  markCopied: PropTypes.func.isRequired,
};

export default Hole;
